// ============================================================================
// Hooks TanStack Query — GRILLE SALARIALE CE (L06, GC-B) : bundle tab GRILLE
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (M-670 / P-CE14, routes/grilleSalariale.ts : garde `canViewSalaryGridCE` =
// role === 'admin' SEUL, director/manager/employee REFUSES). Le SPA consomme le
// bundle assemble ; il ne DECIDE jamais (lecon GA-05). Un 403 serveur devient une
// redirection /error/403, pas un crash.
//
// Le serveur EST RE-FIGE (GC-A merge) — ce module ne fait que CONSOMMER le contrat
// de /api/grille-salariale (GET / = tab GRILLE). Les types MIROITENT les DTO
// serveur a l'identique (server/routes/grilleSalariale.ts GET / + repositories/
// compensation.ts getGridCeEmployeeLines/getPlanningGrid). La query GET / reste
// LECTURE (le shell + le tab grille). GC-C ajoute les 6 MUTATIONS de l'editeur
// (POST/DELETE /classes, load-default, /years, rollover, migrate) — voir la section
// « Mutations editeur » en bas ; chaque ecriture invalide le bundle GRILLE_CE_ROOT_KEY.
// La grille camelCase est adaptee via `adaptGridClasses` (SL-A, partagee avec L05 —
// les champs additifs prevYearRate/description alimentent la rangee « Taux N-1 » +
// les tooltips de SalaryMasterGrid).
//
// [!] GC-C STOP-Q (rapport) : l'editeur edite la grille MASTER (saveSalaryClass ecrit
// grid_scope='master'), mais AUCUN endpoint GET JSON n'expose la master seule —
// `getSalaryGridMaster` (repo) ne sort que par /export (xlsx). GET / renvoie la
// grille resolue par annee : la MASTER tant que l'annee courante n'est PAS figee,
// le SNAPSHOT de l'annee courante APRES migration (M-679). L'editeur consomme donc
// GET / (fidele AUJOURD'HUI — aucune annee figee en prod) ; le jour ou GC-A expose
// GET /master, basculer ici (1 ligne). Escalade au cerveau au merge.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFetch, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import type {
  PlanningGridClass, PlanningGridMember, PlanningGridPosition, PlanningEchelonChange,
  PlanningScenario,
} from './usePlanification';
import type { RealPayrollRow } from '../lib/salary/types';

// ─── Types — MIROIR EXACT du DTO serveur (grilleSalariale.ts GET /) ───────────

/** Role CE (etage 3). Le DTO ne sort admin qu'apres la garde (director => 403). */
export type GestGridCeRole = 'admin' | 'manager' | 'director' | 'employee';

/**
 * Bundle du tab GRILLE (F-657 / M-680) — miroir de la reponse GET /. Les membres
 * positionnes sont GLOBAUX (toute l'entreprise, P-CE13 : lecture remuneration
 * globale, logPiiAccess serveur) ; le client les pivote via buildEmployeeLines.
 */
export interface GrilleCeResponse {
  viewer: { role: GestGridCeRole; email: string };
  /** Annee selectionnee (figees ∪ courante ; hors liste => courante, resolue serveur). */
  year: number;
  /** Annee courante reelle — PIVOT de buildEmployeeLines (passe = annees < currentYear). */
  currentYear: number;
  /** Pivot de projection de la grille (annee figee => taux reels ; master => 2026 projete). */
  gridYear: number;
  /** Annees figees disponibles (decroissantes) — alimente le YearPicker (∪ courante). */
  gridYears: number[];
  /** Grille de l'annee (master si l'annee n'est pas figee) — camelCase, adaptee client. */
  classes: PlanningGridClass[];
  /** Ombrer les inactifs — vrai ssi vue d'une annee PASSEE (selectedYear < currentYear). */
  shadeInactive: boolean;
  /** Nombre de scenarios soumis — pilote le badge rouge de l'onglet Approbations. */
  submittedCount: number;
  /** Membres positionnes GLOBAUX (entree de buildEmployeeLines). */
  members: PlanningGridMember[];
  /** Positions grille passees par email (buildEmployeeLines). */
  pastByEmail: Record<string, PlanningGridPosition[]>;
  /** Historique d'echelon par email (buildEmployeeLines). */
  historyByEmail: Record<string, PlanningEchelonChange[]>;
}

// ─── Query (bundle tab GRILLE) ────────────────────────────────────────────────

const GRILLE_CE_ROOT_KEY = ['gest', 'grille-ce'] as const;
const grilleCeKey = (year: number | null) => [...GRILLE_CE_ROOT_KEY, year ?? 'current'] as const;

/**
 * F-657 : bundle du tab GRILLE (1 requete SPA). `year` optionnel : le SERVEUR
 * resout l'annee (figees ∪ courante ; hors liste => courante — jamais une valeur
 * hors bornes honoree). 403 (non-admin) = etat stable (pas de retry) : la page
 * redirige vers /error/403 (garde client M-670, double la garde serveur).
 */
export function useGrilleCe(year: number | null, enabled: boolean) {
  return useQuery({
    queryKey: grilleCeKey(year),
    queryFn: () =>
      api.get<GrilleCeResponse>('/api/grille-salariale', year != null ? { year: String(year) } : undefined),
    enabled,
    retry: false,
  });
}

// ─── Tab APPROBATIONS (F-658) — bundle GET /approbations (GC-D) ────────────────
// Cle SOUS GRILLE_CE_ROOT_KEY : une decision (useDecideScenario) invalide la
// racine et rafraichit donc CE bundle + le tab grille + les cadres d'un coup.

/** Cout reel par email (miroir MemberRealCost lib) — reconcilie l'impact grille. */
export interface GridCeMemberRealCost {
  realBase: number | null;
  extras: number;
}

/**
 * Bundle du tab APPROBATIONS (F-658 / M-678) — miroir de GET /approbations. Les
 * scenarios `submitted` + les 3 dernieres `recentDecisions` (nominatifs, PII
 * legitime surface CE, P-CE4) ; `classes` = grille de l'annee courante (figee ou
 * master projetee) qui valorise les changements ; `realByEmail` calcule SERVEUR
 * uniquement s'il y a des scenarios soumis (sinon {}).
 */
export interface GrilleCeApprobationsResponse {
  viewer: { role: GestGridCeRole; email: string };
  currentYear: number;
  /** Pivot de projection des taux de `classes` (annee des taux). */
  gridYear: number;
  classes: PlanningGridClass[];
  submitted: PlanningScenario[];
  recentDecisions: PlanningScenario[];
  realByEmail: Record<string, GridCeMemberRealCost>;
}

const grilleCeApprobationsKey = [...GRILLE_CE_ROOT_KEY, 'approbations'] as const;

/** F-658 : bundle du tab APPROBATIONS (1 requete). 403 (non-admin) = etat stable. */
export function useGrilleCeApprobations(enabled: boolean) {
  return useQuery({
    queryKey: grilleCeApprobationsKey,
    queryFn: () => api.get<GrilleCeApprobationsResponse>('/api/grille-salariale/approbations'),
    enabled,
    retry: false,
  });
}

// ─── Tab CADRES (F-660) — bundle GET /cadres (GC-D) ───────────────────────────

/** Resume d'un cadre — miroir EXACT de CadreSummary (compensation.ts GC-A). */
export interface GridCeCadreSummary {
  email: string;
  name: string;
  jobTitle: string | null;
  teamName: string | null;
  configured: boolean;
  classLabel: string | null;
  niveauLabel: string | null;
  echelon: number;
  maxEchelon: number;
  offGrid: boolean;
  salaire: number | null;
  boni: number | null;
  avantages: number | null;
  total: number | null;
  augmentationPct: number | null;
  cumulAugmentationPct: number | null;
  aug1yAmount: number | null;
  aug3yAmount: number | null;
  boniCible: number | null;
  boniCiblePct: number | null;
}

/** Agregats d'un groupe de cadres — miroir EXACT de CadreAggregates (GC-A). */
export interface GridCeCadreAggregates {
  count: number;
  configuredCount: number;
  avgAugmentationPct: number | null;
  avgCumulAugmentationPct: number | null;
  avgAug1yAmount: number | null;
  avgAug3yAmount: number | null;
  masseTotale: number;
  sumSalaire: number;
  sumBoni: number;
  sumAvantages: number;
  sumBoniCible: number;
  remMoyenne: number | null;
  prevYear: number;
}

export interface GridCeCadreGroup {
  summaries: GridCeCadreSummary[];
  aggregates: GridCeCadreAggregates;
}

/** Bundle du tab CADRES (F-660) — miroir de GET /cadres : 2 groupes + sans-niveau. */
export interface GrilleCeCadresResponse {
  viewer: { role: GestGridCeRole; email: string };
  year: number;
  n2: GridCeCadreGroup;
  n1: GridCeCadreGroup;
  sansNiveau: GridCeCadreSummary[];
}

const grilleCeCadresKey = (year: number | null) =>
  [...GRILLE_CE_ROOT_KEY, 'cadres', year ?? 'current'] as const;

/** F-660 : bundle du tab CADRES (annee-aware via le YearPicker). */
export function useGrilleCeCadres(year: number | null, enabled: boolean) {
  return useQuery({
    queryKey: grilleCeCadresKey(year),
    queryFn: () =>
      api.get<GrilleCeCadresResponse>('/api/grille-salariale/cadres', year != null ? { year: String(year) } : undefined),
    enabled,
    retry: false,
  });
}

// ─── Outlook finance de l'annee (miroir YearRevenueOutlook serveur) ───────────
// Le serveur assemble getFinanceYearOutlook (mois reels = dernier ACTUAL, YTD +
// budget des mois restants). `hasData` pilote l'affichage du ratio masse÷revenus
// (VERBATIM reel/page.tsx). Superset structurel du YearRevenueOutlook finance
// (4 champs) que consomment computePayrollRatio/checkBudgetRevenueTarget (client).

export interface GridCeRevenueOutlook {
  actualMonths: number;
  revenueActualYtd: number;
  revenueBudgetYear: number;
  revenueProjected: number;
  hasData: boolean;
}

/** Config des cibles de masse (M-682) — miroir PayrollTargetsConfig serveur. */
export interface GridCeTargetsConfig {
  /** Cible masse÷revenus (ratio) ; null => la page replie DEFAULT_REVENUE_TARGET. */
  revenuePct: number | null;
  /** target_pct (ratio) par team_id — clef de buildTeamTargetRows (client). */
  teamTargets: Record<string, number>;
}

// ─── Tab GRAPHIQUES (F-659) — bundle GET /graphiques (GC-E) ───────────────────

/**
 * Bundle du tab GRAPHIQUES (F-659) — miroir de GET /graphiques. Masse reelle
 * GLOBALE de l'annee (annee pleine, boni agrege a part cote client) + outlook
 * finance + cibles. L'agregation par equipe (aggregateByTeam/buildServiceBreakdowns)
 * et la carte budget (checkBudgetRevenueTarget) se font CLIENT (§a.4, lib pures).
 */
export interface GrilleCeGraphiquesResponse {
  viewer: { role: GestGridCeRole; email: string };
  year: number;
  realRows: RealPayrollRow[];
  revenueOutlook: GridCeRevenueOutlook;
  targetsConfig: GridCeTargetsConfig;
}

const grilleCeGraphiquesKey = (year: number | null) =>
  [...GRILLE_CE_ROOT_KEY, 'graphiques', year ?? 'current'] as const;

/** F-659 : bundle du tab GRAPHIQUES (annee-aware via le YearPicker). */
export function useGrilleCeGraphiques(year: number | null, enabled: boolean) {
  return useQuery({
    queryKey: grilleCeGraphiquesKey(year),
    queryFn: () =>
      api.get<GrilleCeGraphiquesResponse>('/api/grille-salariale/graphiques', year != null ? { year: String(year) } : undefined),
    enabled,
    retry: false,
  });
}

// ─── Page MASSE REELLE (F-667 / M-681) — bundle GET /reel (GC-E) ──────────────

/**
 * Bundle de la page MASSE SALARIALE REELLE (F-667 / M-681) — miroir de GET /reel.
 * Le SERVEUR clippe la periode a l'annee (`start`/`end` retenus) et renvoie AUSSI
 * `fullYearRows` (masse annee pleine recalculee ssi sous-periode) pour le ratio
 * masse÷revenus (dont le numerateur doit etre l'annee pleine, VERBATIM PoC). Les
 * lignes sont GLOBALES (tous employes positionnes, P-CE13, logPiiAccess serveur).
 */
export interface GrilleCeReelResponse {
  viewer: { role: GestGridCeRole; email: string };
  year: number;
  /** Bornes de periode RETENUES serveur (clippees a l'annee, M-681). */
  start: string;
  end: string;
  isFullYear: boolean;
  /** Filtre d'affichage retenu (active = actifs + inactifs touchant la periode). */
  status: 'active' | 'all';
  /** Lignes de la periode selectionnee (proratees). */
  realRows: RealPayrollRow[];
  /** Lignes annee pleine (= realRows si isFullYear) — numerateur du ratio. */
  fullYearRows: RealPayrollRow[];
  revenueOutlook: GridCeRevenueOutlook;
  targetsConfig: GridCeTargetsConfig;
}

const grilleCeReelKey = (params: { year: number | null; start: string | null; end: string | null; status: string | null }) =>
  [...GRILLE_CE_ROOT_KEY, 'reel', params.year ?? 'current', params.start ?? '', params.end ?? '', params.status ?? 'active'] as const;

/**
 * F-667 : bundle de la page MASSE REELLE. Le SERVEUR resout/clippe la periode ;
 * on lui passe les params bruts de l'URL (year/start/end/status) et on lit les
 * valeurs RETENUES dans la reponse (jamais les params bruts). 403 = etat stable.
 */
export function useGrilleCeReel(
  params: { year: number | null; start: string | null; end: string | null; status: string | null },
  enabled: boolean,
) {
  return useQuery({
    queryKey: grilleCeReelKey(params),
    queryFn: () => {
      const q: Record<string, string> = {};
      if (params.year != null) q.year = String(params.year);
      if (params.start) q.start = params.start;
      if (params.end) q.end = params.end;
      if (params.status) q.status = params.status;
      return api.get<GrilleCeReelResponse>('/api/grille-salariale/reel', Object.keys(q).length > 0 ? q : undefined);
    },
    enabled,
    retry: false,
  });
}

// ─── Code d'erreur metier (GA-21 : jamais un code brut a l'ecran) ─────────────

/**
 * Code metier VERBATIM renvoye par le serveur, sinon retombe sur un code generique
 * par statut. La banniere mappe ensuite sur `gest:grilleCe.errors.<code>`.
 */
export function grilleCeErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string' && code.length > 0 && !code.startsWith('GEST_')) return code;
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

/**
 * Codes courts deep-linkables des mutations EDITEUR (P-CE16) : `year_exists` /
 * `empty_master` / `notfound` / `forbidden` / `invalid` / `server`. Le serveur emet
 * des codes `GEST_GRILLE_*` (originalBody.code) ; on les mappe sur les codes courts
 * VERBATIM PoC (le bandeau ?error= de la page d'edition les rend, jamais un code brut).
 * Distinct de grilleCeErrorCode (statut) : le rollover renvoie 400 GEST_GRILLE_YEAR_EXISTS,
 * qui doit rester `year_exists` (pas `invalid`) — sinon la page perd la nuance PoC.
 */
export function gridEditErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string') {
    if (code === 'GEST_GRILLE_YEAR_EXISTS') return 'year_exists';
    if (code === 'GEST_GRILLE_EMPTY_MASTER') return 'empty_master';
    if (code === 'GEST_GRILLE_CLASS_NOT_FOUND') return 'notfound';
    if (code === 'GEST_GRILLE_FORBIDDEN') return 'forbidden';
    if (code === 'GEST_GRILLE_INVALID') return 'invalid';
  }
  if (err instanceof ApiForbiddenError) return 'forbidden';
  return 'server';
}

// ─── Mutations editeur (6) — F-663/665, M-675/676/677/679 (GC-C) ──────────────
// Chaque ecriture invalide GRILLE_CE_ROOT_KEY : le serveur re-assemble la grille
// apres persistance (VERBATIM PoC : saveSalaryClassAction fait revalidatePath des 2
// pages). Les cartes de l'editeur restent montees (key = id de classe stable) — leur
// brouillon local survit au refetch (la carte suit son propre `savedSnapshot`, comme
// le PoC). Le SERVEUR est l'AUTORITE des recalculs (M-675) : le client n'envoie que
// le BRUT (rate, rate_2025, aug en %, labels) ; pct_from_prev, aug %/100 et la
// reindexation 1..n sont recalcules serveur (normalizeSavePayload).

/**
 * Payload editeur (M-675) — MIROIR EXACT de SavePayloadSchema serveur
 * (routes/grilleSalariale.ts). `id` = GUID de la classe MASTER a re-editer, ou ''
 * (nouvelle : le serveur slugify(label) -> class_key). `aug_grille_pct` en
 * POURCENTAGE (ex. 2.5) — le serveur le divise par 100. Le serveur ignore/recalcule
 * tout le reste (echelon #, pct_from_prev).
 */
export interface SalaryClassSavePayload {
  id: string;
  label: string;
  description: string | null;
  sort_order: number;
  aug_grille_pct: number | null;
  base_hours_per_week: number;
  weeks_per_year: number;
  echelons: { rate: number; rate_2025: number | null }[];
  niveaux: { label: string; echelon_max: number | null; description: string | null }[];
}

/** F-663 / M-675 : upsert d'une classe MASTER (POST /classes). Toast cote carte. */
export function useSaveSalaryClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SalaryClassSavePayload) =>
      api.post<{ ok: 'saved'; classKey: string }>('/api/grille-salariale/classes', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

/** F-663 : soft-delete d'une classe MASTER (DELETE /classes/:id, GUID). Toast. */
export function useDeleteSalaryClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: 'deleted' }>(`/api/grille-salariale/classes/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

/** F-663 / M-676 : charge la grille de reference GRILLE 2026 (merge sans perte des
 * customs, serveur). Redirige ?ok=loaded cote page. */
export function useLoadDefaultGrid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: 'loaded' }>('/api/grille-salariale/classes/load-default', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

/**
 * F-665 : upsert d'une classe dans la grille FIGEE d'une annee (POST /years).
 * DORMANT en GC-C : le PoC n'a AUCUN ecran qui l'invoque (les snapshots annuels se
 * construisent via rollover/migrate ; le save-par-annee existe serveur, sans UI).
 * Expose pour parite de contrat (le hook = couche de wiring vers GC-A) — a brancher
 * si un editeur d'annee figee arrive (hors PoC).
 */
export function useSaveSalaryClassYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { year: number; payload: SalaryClassSavePayload }) =>
      api.post<{ ok: 'saved'; classKey: string }>('/api/grille-salariale/years', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

/** F-665 / M-677 : cree la grille N+1 depuis N (POST /years/rollover), idempotent —
 * l'annee cible existante -> 400 GEST_GRILLE_YEAR_EXISTS (gridEditErrorCode). */
export function useRolloverGrid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fromYear: number) =>
      api.post<{ ok: 'rolled'; year: number }>('/api/grille-salariale/years/rollover', {
        from_year: fromYear,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

/** F-665 / M-679 : migration initiale idempotente (POST /years/migrate). Master vide
 * -> 400 GEST_GRILLE_EMPTY_MASTER. */
export function useMigrateAnnualGrids() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: 'migrated'; seededRates: number }>('/api/grille-salariale/years/migrate', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
    },
  });
}

// ─── Decision scenario (F-666 / M-678 / M-654) — GC-D ─────────────────────────
// COHERENCE M-654 (P-CE12) : le lock du board director L05 est PUR CLIENT, derive
// de `pending = scenarios.find(status==='submitted')`. Approuver/rejeter flippe le
// statut serveur → la grille du directeur DOIT se rouvrir SANS action. En SPA, le
// board L05 ne le voit que si SON cache est invalide : la mutation invalide donc
// LES DEUX familles — GRILLE_CE_ROOT_KEY (approbations + grille + cadres) ET la
// racine PLANIFICATION (['gest','planification'], L05). Sans la 2e invalidation, le
// directeur resterait verrouille jusqu'a un refetch fortuit (regression M-654).

/** Racine des queries L05 (usePlanification `PLANIF_ROOT_KEY`, non exporte) —
 * dupliquee VERBATIM ici : toute decision doit rouvrir le board director (M-654). */
const PLANIF_ROOT_KEY = ['gest', 'planification'] as const;

/** Entree de la mutation decide (P-CE16 : la note voyage pour approve ET reject). */
export interface DecideScenarioInput {
  id: string;
  decision: 'approved' | 'rejected';
  decisionNote: string | null;
}

/**
 * F-666 / M-678 : approuve ou rejette un scenario soumis (POST /scenarios/:id/decide).
 * Un scenario deja decide (double-clic, P-CE2) -> 400 GEST_GRILLE_NOT_SUBMITTED
 * (decideErrorCode -> 'not_submitted', bandeau propre). Invalide LES 2 familles
 * (M-654) : la grille CE ET la planification L05.
 */
export function useDecideScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, decisionNote }: DecideScenarioInput) =>
      api.post<{ ok: 'approved' | 'rejected'; changesApplied: number }>(
        `/api/grille-salariale/scenarios/${encodeURIComponent(id)}/decide`,
        { decision, decision_note: decisionNote },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GRILLE_CE_ROOT_KEY });
      void qc.invalidateQueries({ queryKey: PLANIF_ROOT_KEY });
    },
  });
}

/**
 * Code court deep-linkable d'une decision ratee (P-CE16) : `not_submitted` (deja
 * decide / double-clic) / `invalid` / `forbidden` / `server`. Le serveur emet des
 * codes `GEST_GRILLE_*` (originalBody.code) ; le bandeau ?error= les rend, jamais
 * un code brut. `not_submitted` doit rester distinct de `invalid` (nuance M-678 :
 * le scenario existe mais n'est plus soumis) — sinon la page perd le message propre.
 */
export function decideErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string') {
    if (code === 'GEST_GRILLE_NOT_SUBMITTED') return 'not_submitted';
    if (code === 'GEST_GRILLE_INVALID') return 'invalid';
    if (code === 'GEST_GRILLE_FORBIDDEN') return 'forbidden';
  }
  if (err instanceof ApiForbiddenError) return 'forbidden';
  return 'server';
}
